import { ShieldCheckIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import daikinLogo from '../assets/optimized/daikin-logo-svg.webp';
import traneLogo from '../assets/Trane_logo.svg';
import samsungLogo from '../assets/optimized/samsung-old-logo-before-year-2015-svg.webp';
import goodmanLogo from '../assets/goodman-ac-1.svg';
import lennoxLogo from '../assets/optimized/lennox-logo1.webp';
import carrierLogo from '../assets/optimized/logo-of-the-carrier-corporation-svg.webp';
import { useContent } from '../context/ContentContext';

const BRAND_LOGOS: Record<string, { src: string; className: string; width: number; height: number }> = {
  Daikin: { src: daikinLogo, className: 'h-9', width: 360, height: 78 },
  Trane: { src: traneLogo, className: 'h-10', width: 588.0, height: 196.0 },
  Samsung: { src: samsungLogo, className: 'h-10', width: 360, height: 120 },
  Goodman: { src: goodmanLogo, className: 'h-8', width: 77.758, height: 20.571 },
  Lennox: { src: lennoxLogo, className: 'h-9', width: 360, height: 104 },
  Carrier: { src: carrierLogo, className: 'h-12', width: 360, height: 145 },
};

export default function TrustedPartners() {
  const { content, settings } = useContent();
  const tp = content.trustedPartners;

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-brand-off-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-navy/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-brand-orange/5 rounded-full blur-3xl" />

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 xl:top-48">
              <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-brand-orange rounded-full" />
                <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                  {tp.sectionLabel}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
                {tp.heading}
              </h2>

              <p className="text-base sm:text-lg text-[#756c65] leading-relaxed mb-8 sm:mb-10">
                {tp.description}
              </p>

              <div className="hidden sm:block relative mb-10">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-orange to-brand-orange/20 rounded-full" />
                <div className="pl-8">
                  <div className="flex items-center gap-3 mb-4">
                    <ShieldCheckIcon className="w-6 h-6 text-brand-orange" />
                    <h3 className="text-lg font-serif font-bold text-brand-navy">
                      {tp.certifiedHeading}
                    </h3>
                  </div>
                  <div className="space-y-2.5">
                    {tp.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-2.5">
                        <CheckCircleIcon className="w-4 h-4 text-brand-orange flex-shrink-0" />
                        <span className="text-sm text-[#756c65]">{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <a
                href={settings.bookingUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-brand-navy text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold hover:bg-brand-orange transition-colors duration-300 group w-full sm:w-auto"
              >
                <span>{tp.consultationButtonLabel}</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3 sm:gap-5">
              {tp.brands.map((brand, index) => {
                const logo = BRAND_LOGOS[brand.name];
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-7 group hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-brand-orange/20"
                  >
                    <div className="h-10 sm:h-16 flex items-center mb-3 sm:mb-5">
                      {logo && (
                        <img decoding="async" loading="lazy"
                          src={logo.src}
                          width={logo.width}
                          height={logo.height}
                          alt={`${brand.name} logo`}
                          className={`${logo.className} max-h-8 sm:max-h-none w-auto object-contain`}
                        />
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2.5 mb-1 sm:mb-2">
                      <h3 className="font-bold text-brand-navy text-sm sm:text-lg">{brand.name}</h3>
                      <span className="hidden sm:inline text-[10px] font-semibold text-brand-orange bg-brand-orange/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
                        {brand.highlight}
                      </span>
                    </div>
                    <p className="text-brand-warm-gray text-xs sm:text-sm leading-relaxed hidden sm:block">{brand.description}</p>
                    <p className="text-brand-orange text-[10px] font-semibold sm:hidden">{brand.highlight}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
